import pipe from 'lodash/fp/pipe'
import * as Redux from 'redux'
import * as ReduxObservable from 'redux-observable'
import 'rxjs/add/operator/debounceTime'
import 'rxjs/add/operator/distinctUntilChanged'
import 'rxjs/add/operator/filter'
import 'rxjs/add/operator/map'
import 'rxjs/add/operator/switchMap'
import * as App from '../../app'
import * as DetailPanel from '../../components/detail-panel'
import * as FlashMessage from '../../components/flash-message'
import * as Form from '../../components/form'
import * as IsActive from '../../components/is_active'
import * as Menu from '../../components/menu'
import * as Pager from '../../components/pager'
import * as Request from '../../request'
import {ApplicationState} from '../../rootReducer'
import * as Router from '../../router'
import * as CsvUtils from '../../utils'
import * as Schedules from '../schedules'
import * as Actions from './actions'
import * as AddModal from './add-modal/constants'
import * as Constants from './constants'
import * as MemberDetails from './details'
import * as Types from './types'
import * as Utils from './utils'

const onEnter = (
  action$: ReduxObservable.ActionsObservable<Actions.FetchMembersAction>,
  store: Redux.Store<ApplicationState>,
  {history}: App.Dependencies,
) => action$.ofType(Router.ON_ENTER)
  .filter(() => history.location.pathname === Menu.SUBSCRIBERS_URL_HOME)
  .map(store.getState)
  .mergeMap(({app, members}) => [
    Actions.fetchMembers(
      app.selectedCommunity,
      {
        ...Utils.fetchParamsFromState(members),
        ...App.filterObject(app.urlParams, Constants.MEMBERS_FILTER_FORM_INITIAL_VALUES),
      },
    ),
    Actions.FilterFormActions[Form.ENTER]({
      ...Utils.fetchParamsFromState(members),
      ...App.filterObject(app.urlParams, Constants.MEMBERS_FILTER_FORM_INITIAL_VALUES),
    }),
  ])
const getExportParam = (history: any) => {
  const qs = history.location.search
  const p = new URLSearchParams(qs)
  return p.get('_export')
}
const onExportEnter = (
  action$: ReduxObservable.ActionsObservable<Actions.FetchMembersAction>,
  store: Redux.Store<ApplicationState>,
  {history}: App.Dependencies,
) => action$.ofType(Router.ON_ENTER)
  .filter(() => history.location.pathname === Menu.SUBSCRIBERS_URL_HOME && !!getExportParam(history))
  .map(() => {
    const url = decodeURI(getExportParam(history))
    return Router.redirect(url)
  })

const onGoToPage = (
  action$: ReduxObservable.ActionsObservable<Actions.FetchMembersAction>,
  store: Redux.Store<ApplicationState>,
  {history}: App.Dependencies,
) => action$.ofType(
    Pager.goToPageConstant(Constants.FETCH_MEMBERS),
  )
  .map(store.getState)
  .mergeMap(({app, members}) => [
    Actions.fetchMembers(
      app.selectedCommunity,
      Utils.fetchParamsFromState(members)),
    App.updateUrlParams(Utils.fetchParamsFromState(members)),
  ])

const onFetchPageOneSuccess = (
  action$: ReduxObservable.ActionsObservable<Actions.FetchMembersAction>,
  store: Redux.Store<ApplicationState>,
  {history}: App.Dependencies,
) => action$.ofType(
    Request.successConstant(Constants.FETCH_MEMBERS),
  )
  .map(store.getState)
  .filter(({members}) => (members.pager.pageNumber || 1) <= 1 )
  .map(({app, members}) => Actions.fetchMemberCounts(
      app.selectedCommunity,
      Utils.fetchParamsFromState(members)))

const onSave = (
  action$: ReduxObservable.ActionsObservable<Actions.FetchMembersAction>,
  store: Redux.Store<ApplicationState>,
  {history}: App.Dependencies,
) => action$.ofType(
    Constants.FilterFormConstants[Form.SAVE],
  )
  .map(store.getState)
  .map(({members}) => App.updateUrlParams(Utils.fetchParamsFromState(members)))

const sortBy = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Constants.SORT_MEMBERS)
  .map(store.getState)
  .map(({members}) => App.updateUrlParams({
    direction: members.view.sortBy.direction,
    sort: members.view.sortBy.field,
  }))

const onSearch = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Constants.ON_SEARCH)
  .mergeMap((action) => [
    Actions.FilterFormActions[Form.UPDATE]({name: 'search_term', value: action.value}),
    Actions.searchFinished(action.value),
  ])

const onSearchSuccess = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Constants.ON_SEARCH_FINISHED)
  .filter(({value}) => value.length > 1 || value.length === 0)
  .debounceTime(500)
  .distinctUntilChanged()
  .mergeMap((action) => [
    Actions.fetchMembers(
      store.getState().app.selectedCommunity,
      Utils.fetchParamsFromState(store.getState().members)),
  ])

const onExportSuccess = (
    action$: ReduxObservable.ActionsObservable<any>,
    store: Redux.Store<ApplicationState>,
  ) => action$.ofType(Request.successConstant(Constants.EXPORT_SUBSCRIBERS))
    .filter((action) => action.payload.response.status.includes('done'))
    .map((action) => {
        return CsvUtils.downloadCSVContent(action.payload.response.data)
    })
const onExportPending = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Request.successConstant(Constants.EXPORT_SUBSCRIBERS))
  .filter((action) => !action.payload.response.status.includes('done'))
  .map(() => {
      return FlashMessage.showFlashMessage(Constants.EXPORT_ASYNC_MESSAGE)
  })

const openDetailPanelEpic = pipe(
  DetailPanel.createOnOpen(MemberDetails.MEMBER_DETAIL_PREFIX),
  (action$) => action$
    .filter(({action, state}) => state.members.detailPanel.isOpen)
    .mergeMap(({action, state}) => [
      Actions.fetchMemberDetails(state.app.selectedCommunity, action.value.id),
      MemberDetails.DetailPanelActions[DetailPanel.LOAD](true),
    ]),
)

const onSaveRequest = (
  action$: ReduxObservable.ActionsObservable<Form.SaveAction<Types.Member>>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(MemberDetails.FormConstants[Form.SAVE])
  .map((action) => Actions.update(store.getState().app.selectedCommunity, action.value))

// TODO: I'd like onSaveSuccess not to re-query.  We should be able to update
// local state via a reducer.
const onAddSuccess = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Request.successConstant(AddModal.ADD_MEMBER))
  .mergeMap((action) => ([
    FlashMessage.showFlashMessage('Success!'),
    Actions.fetchMembers(
      store.getState().app.selectedCommunity,
      Utils.fetchParamsFromState(store.getState().members)),
  ]))

const onSaveSuccess = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Request.successConstant(Constants.UPDATE_MEMBER))
  .mergeMap((action) => ([
    MemberDetails.EditFieldActions[Form.EXIT](action),
    FlashMessage.showFlashMessage('Success!'),
    Actions.fetchMembers(
      store.getState().app.selectedCommunity,
      Utils.fetchParamsFromState(store.getState().members)),
      MemberDetails.DetailPanelActions[DetailPanel.OPEN]({
        ...store.getState().members.memberDetail,
        ...store.getState().members.detailForm.fields,
      }),
  ]))

const onSaveFail = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Request.errorConstant(Constants.UPDATE_MEMBER))
  .mergeMap((action) => ([
    MemberDetails.EditFieldActions[Form.CANCEL](action),
    FlashMessage.showFlashError(),
  ]))

const onSort = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Constants.SORT_MEMBERS)
  .map((action) => Actions.fetchMembers(
  store.getState().app.selectedCommunity,
  Utils.fetchParamsFromState(store.getState().members)),
)

const onEdit = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: Redux.Store<ApplicationState>,
) => action$.ofType(Constants.ON_EDIT)
  .map((action) => Router.push(`${Menu.SUBSCRIBERS_SUBSCRIBER_DETAIL_URL}/${action.identifier}`))

export const epic = ReduxObservable.combineEpics(
  onAddSuccess,
  onEdit,
  onGoToPage,
  onSaveFail,
  onSaveRequest,
  onSaveSuccess,
  onEnter,
  onExportEnter,
  openDetailPanelEpic,
  onExportSuccess,
  onExportPending,
  onFetchPageOneSuccess,
  onSave,
  onSearch,
  onSearchSuccess,
  onSort,
  IsActive.createStatusUpdateEpic(Constants.UPDATE_MEMBER_INLINE, Actions.updateIsActive),
  Pager.pageChangeEpic(Constants.FETCH_MEMBERS),
  Schedules.pageLoadEpic(Menu.SUBSCRIBERS_URL_HOME),
  sortBy,
)



// WEBPACK FOOTER //
// ./src/pages/members/epic.ts